<template>
  <div class="w-full" :class="{ 'android-video-wrapper': isAndroid }">
    <video
      :ref="`video-player-${id}`"
      :class="cssClasses"
      class="video-js w-full vjs-big-play-centered overflow-hidden"
    ></video>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-quality-levels';
import '@mycujoo/videojs-hls-quality-selector';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { trackEvent } from '@/utils/mixpanel';
import { getConferenceType } from '@/utils/conference-type';
import { isAndroidNativeOnly } from '@/utils/platform-check';
import { SafeArea } from '@capacitor-community/safe-area';
import { ScreenOrientation } from '@capacitor/screen-orientation';
// videojs.Vhs.GOAL_BUFFER_LENGTH = 30;
// videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = 60 * 5;

// Get the Component base class from Video.js
const Component = videojs.getComponent('Component');

// ES6 class extending Component (videojs.extend was removed in v8)
class MyButton extends Component {
  constructor(player, options) {
    super(player, options);

    if (options.text) {
      this.updateTextContent(options.text);
    }
  }

  createEl() {
    return videojs.dom.createEl('div', {
      className: 'vjs-button',
    });
  }

  updateTextContent(text) {
    if (typeof text !== 'string') {
      text = 'Title Unknown';
    }
    videojs.dom.emptyEl(this.el());
    videojs.dom.appendContent(this.el(), text);
  }
}

// Register the component with Video.js, so it can be used in players.
videojs.registerComponent('MyButton', MyButton);

export default {
  name: 'VideoPlayer',
  props: {
    metadata: {
      type: Object,
      required: true,
      validator(metadata) {
        return metadata.url && metadata.mime && metadata.thumbnail && metadata.title;
      },
    },
    id: {
      type: String,
      required: true,
    },
    cssClasses: {
      type: String,
      default: '',
    },
    currentVideoTime: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      player: null,
      lastTimeUpdate: 0,
      accumulatedTimeWatched: 0,
      sendInterval: 60, // in seconds
      progressTracked: [], // Keeps track of the progress points already tracked
      tenFlag: false,
      thrityFlag: false,
      fiftyFlag: false,
      seventyFlag: false,
      ninetyFlag: false,
      androidNavBarHeight: 0,
    };
  },
  computed: {
    isAndroid() {
      return isAndroidNativeOnly();
    },
    isIosOrIpad() {
      const isIOSDevice = /iphone|ipad|ipod/i.test(navigator.userAgent);
      const isIPadOS =
        /macintosh/i.test(navigator.userAgent) &&
        'ontouchend' in document &&
        navigator.maxTouchPoints > 0;
      return isIOSDevice || isIPadOS;
    },
    shouldUseNativeHls() {
      const url = this.metadata.url;
      const isHls = url.includes('.m3u8') || url.includes('/hls/');
      return this.isIosOrIpad && isHls;
    },
    playerOptions() {
      return {
        autoplay: false,
        controls: true,
        playsinline: true,
        playbackRates: [0.5, 1, 1.5, 2],
        poster: this.metadata.thumbnail,
        sources: [
          {
            src: this.metadata.url,
            type: this.metadata.mime,
          },
        ],
        fluid: true,
        html5: {
          nativeTextTracks: false, // TODO: Need to make this dynamic when we have subtitles. Hard code it temporary to fix an iOS issue where it shows "Unknown CC"
          vhs: {
            experimentalBufferBasedABR: true,
            overrideNative: !this.shouldUseNativeHls,
          },
          hls: {
            limitRenditionByPlayerDimensions: true,
            useDevicePixelRatio: true,
            bandwidth: 6194304,
          },
        },
      };
    },
  },
  mounted() {
    if (this.isAndroid) {
      this.fetchAndroidNavBarHeight();
    }
    // console.log('[VideoPlayer] isIosOrIpad:', this.isIosOrIpad);
    // console.log('[VideoPlayer] shouldUseNativeHls:', this.shouldUseNativeHls);
    // console.log('[VideoPlayer] overrideNative:', !this.shouldUseNativeHls);
    // console.log('[VideoPlayer] url:', this.metadata.url);
    this.player = videojs(this.$refs[`video-player-${this.id}`], this.playerOptions, () => {
      // this.player.on('error', () => {
      //   const err = this.player.error();
      //   console.error('[VideoPlayer] error event fired');
      //   console.error('[VideoPlayer] err code:', err?.code);
      //   console.error('[VideoPlayer] err message:', err?.message);
      //   console.error('[VideoPlayer] currentSrc:', this.player.currentSrc());
      // });

      this.$nextTick(() => {
        const videoEl = this.player.tech()?.el();
        // console.log('[VideoPlayer] document.fullscreenEnabled:', document.fullscreenEnabled);
        // console.log('[VideoPlayer] document.webkitFullscreenEnabled:', document.webkitFullscreenEnabled);
        // console.log('[VideoPlayer] video.webkitSupportsFullscreen:', videoEl?.webkitSupportsFullscreen);

        if (this.shouldUseNativeHls) {
          // Force native iOS fullscreen — Capacitor 8 exposes document.fullscreenEnabled=true
          // which causes Video.js to use CSS requestFullscreen() instead of webkitEnterFullscreen().
          this.player.requestFullscreen = () => {
            const nativeVideoEl = this.player.tech()?.el();
            // console.log('[VideoPlayer] requestFullscreen override called, webkitEnterFullscreen:', !!nativeVideoEl?.webkitEnterFullscreen);
            if (nativeVideoEl?.webkitEnterFullscreen) {
              nativeVideoEl.webkitEnterFullscreen();
            }
          };
          if (videoEl) {
            videoEl.addEventListener('webkitbeginfullscreen', this.onEnterFullscreen);
            videoEl.addEventListener('webkitendfullscreen', this.onExitFullscreen);
          }
        }
      });
    });
    localStorage.setItem('currentVideoId', this.id);
    // console.log(this.isSafari());
    // if (!this.isSafari()) {
    //   this.player.hlsQualitySelector({
    //     displayCurrentQuality: true,
    //   });
    // }
    // const qualityLevels = this.player.qualityLevels();

    // qualityLevels.on("addqualitylevel", function (event) {
    //   const qualityLevel = event.qualityLevel;

    //   qualityLevel.enabled = qualityLevel.height >= 720;
    //   if (qualityLevel.height < 720) {
    //     qualityLevels.removeQualityLevel(qualityLevel);
    //   }
    // });
    this.addVideoListeners();
    // this.player.player.ready(() => {
    //   player.currentTime(startTime) // Jump to startTime
    //   player.play() // Optional: autoplay from that time
    // })

    this.player.on('pause', () => {
      console.log('pause');
      // flush watch time on pause; guard skips the auto-pause fired just before 'ended'
      if (this.accumulatedTimeWatched >= 1) {
        this.sendAccumulatedTimeWatched();
      }
      // put currentTime in local storage
      localStorage.setItem('currentVideoTime', this.player.currentTime());
      localStorage.setItem('currentVideoDuration', this.player.duration());

      console.log('currenttime', this.player.currentTime());
      console.log('current time puase event');
      this.$emit('pauseEvent', 'Data from child');
    });

    // this.player.on("timeupdate", function () {
    //   localStorage.setItem(`currentVideoTime`, this.player.currentTime());
    // });

    this.player.on('play', () => {
      console.log('video started...');
      localStorage.setItem('currentVideoTime', this.player.currentTime());

      const videoTitle = this.player
        .currentSrc()
        .substring(this.player.currentSrc().indexOf('.com/') + 1);
      FirebaseAnalytics.logEvent({
        name: 'video_play',
        params: {
          video_title: videoTitle,
          video_status: 'start',
          video_provider: 'html',
        },
      });
      trackEvent('Video Played', {
        video_title: videoTitle,
        video_status: 'start',
        video_provider: 'html',
      });
      this.$emit('playEvent', 'Data from child');
    });
    this.player.on('ended', () => {
      console.log('video ended...');
      const videoTitle = this.player
        .currentSrc()
        .substring(this.player.currentSrc().indexOf('.com/') + 1);
      FirebaseAnalytics.logEvent({
        name: 'video_play',
        params: {
          video_title: videoTitle,
          video_status: 'completed',
          video_provider: 'html',
        },
      });
      trackEvent('Video Played', {
        video_title: videoTitle,
        video_status: 'completed',
        video_provider: 'html',
      });
    });
    this.player.on('progress', () => {
      const percentage = Math.round((this.player.currentTime() / this.player.duration()) * 100);

      if (percentage >= 90 && !this.ninetyFlag) {
        const videoTitle = this.player
          .currentSrc()
          .substring(this.player.currentSrc().indexOf('/') + 1);
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '90',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '90',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.ninetyFlag = true;
      } else if (percentage >= 70 && !this.seventyFlag) {
        const videoTitle = this.player
          .currentSrc()
          .substring(this.player.currentSrc().indexOf('/') + 1);
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '70',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '70',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.seventyFlag = true;
      } else if (percentage >= 50 && !this.fiftyFlag) {
        const videoTitle = this.player
          .currentSrc()
          .substring(this.player.currentSrc().indexOf('/') + 1);
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '50',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '50',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.fiftyFlag = true;
      } else if (percentage >= 30 && !this.thirtyFlag) {
        const videoTitle = this.player
          .currentSrc()
          .substring(this.player.currentSrc().indexOf('/') + 1);
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '30',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '30',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.thirtyFlag = true;
      } else if (percentage >= 10 && !this.tenFlag) {
        const videoTitle = this.player
          .currentSrc()
          .substring(this.player.currentSrc().indexOf('/') + 1);
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '10',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '10',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.tenFlag = true;
      }
    });
  },
  beforeUnmount() {
    if (this.player) {
      this.sendAccumulatedTimeWatched();

      if (this._safeAreaUnlockTimeout) clearTimeout(this._safeAreaUnlockTimeout);
      const videoEl = this.player.tech()?.el();
      if (videoEl) {
        videoEl.removeEventListener('webkitbeginfullscreen', this.onEnterFullscreen);
        videoEl.removeEventListener('webkitendfullscreen', this.onExitFullscreen);
      }
      if (this.isAndroid) {
        this.player.off('fullscreenchange', this.onAndroidFullscreenChange);
        ScreenOrientation.lock({ orientation: 'portrait' });
      }

      this.player.dispose();
    }
  },
  methods: {
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },
    addVideoListeners() {
      // Reset baseline on seek so timeupdate delta starts from the destination, not the origin.
      // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/seeking_event
      this.player.on('seeking', () => {
        this.lastTimeUpdate = this.player.currentTime();
      });
      this.player.on('timeupdate', this.trackWatchDuration);
      this.player.on('ended', this.trackVideoEnd);
      this.player.on('play', this.trackVideoStart);
      if (this.isAndroid) {
        this.player.on('fullscreenchange', this.onAndroidFullscreenChange);
      }
    },
    async fetchAndroidNavBarHeight() {
      try {
        const { insets } = await SafeArea.getSafeAreaInsets();
        this.androidNavBarHeight = insets.bottom;
      } catch {
        // fallback: estimate from screen vs viewport height difference
        this.androidNavBarHeight = window.screen.height - window.innerHeight;
      }
    },
    onAndroidFullscreenChange() {
      const controlBar = this.player.controlBar?.el();
      if (!controlBar) return;
      if (this.player.isFullscreen()) {
        ScreenOrientation.unlock();
        controlBar.style.paddingBottom = `${this.androidNavBarHeight}px`;
      } else {
        ScreenOrientation.lock({ orientation: 'portrait' });
        controlBar.style.paddingBottom = '';
      }
    },
    trackWatchDuration() {
      const currentTime = this.player.currentTime();
      const totalDuration = this.player.duration();

      // Calculate time watched
      const delta = currentTime - this.lastTimeUpdate;
      if (delta > 0) {
        this.accumulatedTimeWatched += delta;
      }

      // Track progress
      const progressPoints = [0.1, 0.25, 0.5, 0.75, 0.9, 1];
      for (const point of progressPoints) {
        if (currentTime >= point * totalDuration && !this.progressTracked.includes(point)) {
          this.trackVideoProgress(point);
          this.progressTracked.push(point);
        }
      }

      // Send event to Google Analytics 4 every 60 seconds
      if (this.accumulatedTimeWatched >= this.sendInterval) {
        this.sendAccumulatedTimeWatched();
      }

      this.lastTimeUpdate = currentTime;
    },
    trackVideoProgress(progress) {
      const progressPercentage = progress * 100;

      if (window.gtag) {
        window.gtag('event', 'video_progress', {
          video_title: this.metadata.title,
          progress_percentage: progressPercentage,
        });
      }
    },
    trackVideoStart() {
      if (this.player.currentTime() <= 0) {
        // Reset progress tracking when video starts over
        this.progressTracked = [];

        if (window.gtag) {
          window.gtag('event', 'video_start', {
            video_title: this.metadata.title,
          });
        }
      }
    },
    trackVideoEnd() {
      this.sendAccumulatedTimeWatched();

      if (window.gtag) {
        window.gtag('event', 'video_end', {
          video_title: this.metadata.title,
        });
      }
    },
    seek(seconds) {
      this.player.currentTime(seconds);
    },
    play() {
      this.player.play();
    },
    sendAccumulatedTimeWatched() {
      if (window.gtag) {
        window.gtag('event', 'video_watch_duration', {
          video_title: this.metadata.title,
          time_watched: this.accumulatedTimeWatched,
        });
      }

      try {
        trackEvent('Video Duration', {
          video_id: this.id,
          video_title: this.metadata.title,
          video_length: Number.isFinite(this.player.duration())
            ? this.player.duration()
            : undefined,
          time_watched: this.accumulatedTimeWatched,
          page_url: this.$route?.path,
          conference: getConferenceType(),
        });
      } catch {
        // noop
      }

      this.accumulatedTimeWatched = 0;
    },
    onEnterFullscreen() {
      const root = document.documentElement;
      const computed = getComputedStyle(root);
      this._savedSafeArea = {
        top: computed.getPropertyValue('--ion-safe-area-top').trim(),
        bottom: computed.getPropertyValue('--ion-safe-area-bottom').trim(),
        left: computed.getPropertyValue('--ion-safe-area-left').trim(),
        right: computed.getPropertyValue('--ion-safe-area-right').trim(),
      };
      // console.log('[VideoPlayer] webkitbeginfullscreen fired, saved safe area:', this._savedSafeArea);
    },
    onExitFullscreen() {
      // console.log('[VideoPlayer] webkitendfullscreen fired, restoring safe area:', this._savedSafeArea);
      const root = document.documentElement;
      if (this._savedSafeArea) {
        root.style.setProperty('--safe-area-inset-top', this._savedSafeArea.top);
        root.style.setProperty('--safe-area-inset-bottom', this._savedSafeArea.bottom);
        root.style.setProperty('--safe-area-inset-left', this._savedSafeArea.left);
        root.style.setProperty('--safe-area-inset-right', this._savedSafeArea.right);
      }
      clearTimeout(this._safeAreaUnlockTimeout);
      this._safeAreaUnlockTimeout = setTimeout(() => {
        // console.log('[VideoPlayer] safe area unlock timeout fired, removing overrides');
        root.style.removeProperty('--safe-area-inset-top');
        root.style.removeProperty('--safe-area-inset-bottom');
        root.style.removeProperty('--safe-area-inset-left');
        root.style.removeProperty('--safe-area-inset-right');
      }, 500);
    },
  },
};
</script>
